import React, { useCallback, useEffect, useState } from 'react';
import FormField from '../../components/FormField/FormField';
import { adminErrorMessage, fetchAdminUsers } from '../../utils/adminApi';
import { formatClient } from '../../utils/adminAuditLog';
import './admin.css';

const PAGE_SIZE = 50;

const CLIENTS = [
    { id: '', label: 'All clients' },
    { id: 'web', label: 'Web' },
    { id: 'android', label: 'App' },
];

function formatDate(iso) {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleString(undefined, {
            dateStyle: 'medium',
            timeStyle: 'short',
        });
    } catch {
        return iso;
    }
}

export default function AdminUsers() {
    const [client, setClient] = useState('');
    const [qInput, setQInput] = useState('');
    const [q, setQ] = useState('');
    const [offset, setOffset] = useState(0);
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const applySearch = useCallback(() => {
        setQ(qInput.trim().toLowerCase());
        setOffset(0);
    }, [qInput]);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await fetchAdminUsers({
                limit: PAGE_SIZE,
                offset,
                client,
                q,
            });
            setItems(data.users || []);
            setTotal(data.total || 0);
        } catch (e) {
            setError(adminErrorMessage(e.code || e.message));
        } finally {
            setLoading(false);
        }
    }, [offset, client, q]);

    useEffect(() => {
        load();
    }, [load]);

    const page = Math.floor(offset / PAGE_SIZE) + 1;
    const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

    return (
        <>
            <div className="admin__controls admin__controls--wrap">
                <FormField label="Client" htmlFor="admin-users-client" className="form-field--fixed">
                    <select
                        id="admin-users-client"
                        className="field"
                        value={client}
                        onChange={(e) => {
                            setClient(e.target.value);
                            setOffset(0);
                        }}
                    >
                        {CLIENTS.map((c) => (
                            <option key={c.id || 'all'} value={c.id}>{c.label}</option>
                        ))}
                    </select>
                </FormField>
                <FormField label="Search" htmlFor="admin-users-q" className="form-field--fixed">
                    <input
                        id="admin-users-q"
                        type="search"
                        className="field"
                        placeholder="name or kerberos"
                        value={qInput}
                        onChange={(e) => setQInput(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') applySearch();
                        }}
                        onBlur={applySearch}
                    />
                </FormField>
                <div className="admin__control admin__control--end">
                    {!loading && (
                        <span className="admin__count tnum">{total} user{total === 1 ? '' : 's'}</span>
                    )}
                    {(client || q) && (
                        <button
                            type="button"
                            className="btn btn--ghost btn--sm"
                            onClick={() => {
                                setClient('');
                                setQInput('');
                                setQ('');
                                setOffset(0);
                            }}
                        >
                            Clear filters
                        </button>
                    )}
                    <button type="button" className="btn btn--ghost btn--sm" onClick={load}>
                        Refresh
                    </button>
                </div>
            </div>

            {error && (
                <div className="admin__body-pad">
                    <p className="status status--err">{error}</p>
                </div>
            )}

            {loading && (
                <p className="admin__loading admin__body-pad" role="status" aria-live="polite">
                    Loading users…
                </p>
            )}

            {!loading && items.length === 0 && !error && (
                <p className="admin__empty">No users match these filters.</p>
            )}

            {!loading && items.length > 0 && (
                <div className="admin__table-wrap admin__table-wrap--flush">
                    <table className="admin__table">
                        <thead>
                            <tr>
                                <th scope="col">User</th>
                                <th scope="col">Client</th>
                                <th scope="col">Last login</th>
                                <th scope="col">First login</th>
                                <th scope="col">Logins</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((row) => (
                                <tr key={row.kerberos}>
                                    <td>
                                        {row.name
                                            && row.name.toLowerCase() !== row.kerberos.toLowerCase()
                                            ? (
                                                <>
                                                    <div>{row.name}</div>
                                                    <div className="mono admin__target-kind">{row.kerberos}</div>
                                                </>
                                            )
                                            : (
                                                <span className="mono">{row.kerberos}</span>
                                            )}
                                    </td>
                                    <td className="mono">{formatClient(row.client)}</td>
                                    <td className="mono">{formatDate(row.lastLoginAt)}</td>
                                    <td className="mono">{formatDate(row.firstLoginAt)}</td>
                                    <td className="mono tnum">{row.loginCount}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {total > PAGE_SIZE && (
                <div className="admin__pager admin__body-pad">
                    <span className="tnum">
                        Page {page} of {pageCount} · {total} total
                    </span>
                    <span className="admin__control admin__control--end">
                        <button
                            type="button"
                            className="btn btn--ghost btn--sm"
                            disabled={offset === 0}
                            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                        >
                            Previous
                        </button>
                        <button
                            type="button"
                            className="btn btn--ghost btn--sm"
                            disabled={offset + PAGE_SIZE >= total}
                            onClick={() => setOffset(offset + PAGE_SIZE)}
                        >
                            Next
                        </button>
                    </span>
                </div>
            )}
        </>
    );
}
